/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {isCurrentTheme, useThemeMode} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import {getFormattedDate} from '@ion/dls/src/utils/ComponentUtils';
import {getCurrentSensorHistoryStyles} from './sensorHistoryStyles';

type TiltSensorHistoryItemProps = {
  item: GenericObject;
  index: number;
  maxTip?: number;
};

const useStyles = makeStyles((theme: DLSTheme) => {
  const {historyItem, reading, eventText, historyItemTime} = getCurrentSensorHistoryStyles();
  return {
    historyItem : {
      ...dlsTypography('chipButton')
    },
    historyItemEvent : {
      color : dlsColor('caGreen')
    },
    historyItemTime : {
      color : dlsColor('grey3')
    },
    tiltReading : {
      color         : dlsColor('grey1'),
      textTransform : 'uppercase'
    },
    // current theme renders each history row as the shared history tile -
    // the event line keeps its legacy inline status color ...
    currentHistoryItem     : historyItem,
    currentTiltReading     : reading,
    currentEventText       : eventText,
    currentHistoryItemTime : historyItemTime
  };
});

const TiltSensorHistoryItem = ({
  item,
  index,
  maxTip
}: TiltSensorHistoryItemProps) => {
  const classes             = useStyles();
  const isCurrent           = isCurrentTheme();
  const {mode}              = useThemeMode();
  const tilt                = translate('tracking:scTiltSensor.tilt');
  const status              = translate('tracking:scTiltSensor.status');
  const alarm               = translate('tracking:scTiltSensor.alarm');
  const normalEvent         = `${status}: ${item?.y}° ${tilt}`;
  const alarmEvent          = `${status}: ${alarm} (${item?.y}° ${tilt})`;
  const isThresholdBreached = item?.value < maxTip;
  const historyItemColor    = useMemo(() => (
    isThresholdBreached ? dlsColor('caBlue') : dlsColor('caRed')
  ), [isThresholdBreached, mode]);
  const eventText           = useMemo(() => isThresholdBreached ? normalEvent : alarmEvent, [isThresholdBreached]);

  return (
    <div className={themedClassName(classes.historyItem, classes.currentHistoryItem, isCurrent)}
      key={index}>
      <div className={themedClassName(classes.tiltReading, classes.currentTiltReading, isCurrent)}>
        {translate('tracking:scTiltSensor.reading')}
      </div>
      <div className={isCurrent ? classes.currentEventText : undefined}
        style={{color: `${historyItemColor}`}}>
        {eventText}
      </div>
      <div className={themedClassName(classes.historyItemTime, classes.currentHistoryItemTime, isCurrent)}>
        {getFormattedDate(item?.eventTime)}
      </div>
    </div>
  );
};

export default TiltSensorHistoryItem;
